/* eslint-disable max-lines-per-function */
import './Form.scss'

import { SvgIcon } from '@bob/library'
import { AppProviderContext } from '@websrc/App/AppContext'
import { trackSubmitForm } from '@websrc/App/Helpers/tracking'
import { useLocale } from '@websrc/App/Hooks/useLocale'
import { matomo } from '@websrc/App/Tracking/Matomo'
import { mapForm } from '@websrc/DataMappers/mapSpot'
import React, { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { Markdown } from '../Markdown/Markdown'
import { PageMessage } from '../PageMessage/PageMessage'

export const ScriptForm: FC<
  Omit<ReturnType<typeof mapForm>, 'script' | 'realTimeFormScript'> & {
    script: string
  }
> = ({
  description,
  headline,
  script,
  tracking_enabled,
  action_on_submit,
  submit_page_message,
  submit_redirect_url,
  trackingId,
}) => {
  const locale = useLocale()
  const { matomoPageTemplate } = useContext(AppProviderContext)
  const [showPageMessage, setShowPageMessage] = useState(false)
  const formRef = useRef<HTMLDivElement>(null)
  const pageMessageRef = useRef<HTMLDivElement>(null)

  const handleSubmit = useCallback(() => {
    if (action_on_submit === 'Show message') {
      const offsetTop = pageMessageRef.current?.offsetTop
      window.scrollTo({ top: offsetTop, behavior: 'smooth' })
      setShowPageMessage(true)
    } else if (action_on_submit === 'Redirect to a new page') {
      const redirectUrl = submit_redirect_url?.startsWith('/')
        ? submit_redirect_url.slice(1)
        : `${submit_redirect_url}`
      if (redirectUrl) {
        window.location.href = `/${locale}/${redirectUrl}`
      }
    }
  }, [action_on_submit, locale, submit_redirect_url])

  const mapFormScript = useMemo(() => {
    const findDivElements = script.match(/<div.*?>.*?<\/div>/gs)
    const findScriptElements = script.match(/<script.*?>.*?<\/script>/gs)

    // if the script is not valid, return null
    if (findDivElements?.length !== 3 || findScriptElements?.length !== 2) {
      return null
    }

    const formBlockId = findDivElements[0].match(/data-form-block-id=\\?"\n?(.*?)\"/)?.[1]
    const formLoaderScript = findScriptElements[0].match(/src=\\?"\n?(.*?)\"/)?.[1]
    const wsTrackingScript = findScriptElements[1].match(/src=\\?"\n?(.*?)\"/)?.[1]
    const id = findDivElements[1].match(/id="(.*?)"/)?.[1]
    const websiteId = findDivElements[2].match(/data-website-id=\\?"\n?(.*?)\"/)?.[1]
    const hostname = findDivElements[2].match(/data-hostname=\\?"\n?(.*?)\"/)?.[1]

    // if the script is not valid, return null
    if (!formBlockId || !formLoaderScript || !wsTrackingScript || !id || !websiteId || !hostname) {
      return null
    }

    return {
      formBlockId,
      formLoaderScript,
      script1: findScriptElements[1],
      wsTrackingScript,
      id,
      websiteId,
      hostname,
    }
  }, [script])

  // add the script to the DOM
  useEffect(() => {
    if (mapFormScript?.formLoaderScript && mapFormScript?.wsTrackingScript) {
      const formLoaderScript = document.createElement('script')
      const wsTrackingScript = document.createElement('script')

      formLoaderScript.src = mapFormScript?.formLoaderScript
      wsTrackingScript.src = mapFormScript?.wsTrackingScript
      formLoaderScript.async = true
      wsTrackingScript.async = true

      const formLoaderScriptElement = document.body.appendChild(formLoaderScript)
      document.body.appendChild(wsTrackingScript)

      formLoaderScriptElement.addEventListener('load', () => {
        ;(window as any).MsCrmMkt.MsCrmFormLoader.on('afterFormSubmit', () => {
          handleSubmit()
          tracking_enabled && trackSubmitForm()
          matomo.trackEvent('conversion', matomoPageTemplate, ['form'], [headline], 'form')
          matomo.trackCoralEvent({
            eventAction: 'form-submit',
            eventCategory: 'sign-up',
            eventName: trackingId ? 'trackingId: ' + trackingId : '',
          })
        })
        ;(window as any).MsCrmMkt.MsCrmFormLoader.on('afterFormLoad', () => {
          const scriptForm = document.querySelector('.scriptForm')
          const form = scriptForm?.querySelector('form')
          const inputs = form?.querySelectorAll('input')
          const submitButton = form?.querySelector('button[type="submit"]')

          if (!inputs || !form) return

          inputs?.forEach((input) => {
            input.addEventListener('blur', function () {
              if (!input.checkValidity()) {
                input.classList.add('invalid')
              }
            })

            input.addEventListener('input', function () {
              if (input.checkValidity()) {
                input.classList.remove('invalid')
              }
            })
          })

          submitButton?.addEventListener('click', function () {
            inputs.forEach((input) => {
              if (!input.checkValidity()) {
                input.classList.add('invalid')
              }
            })
          })
        })
      })

      return () => {
        document.body.removeChild(formLoaderScript)
        document.body.removeChild(wsTrackingScript)
        ;(window as any).MsCrmMkt = undefined // ms crm form loader clean up
      }
    }
  }, [
    mapFormScript?.formLoaderScript,
    mapFormScript?.wsTrackingScript,
    handleSubmit,
    tracking_enabled,
    matomoPageTemplate,
    headline,
    trackingId,
  ])

  const showSubmitMessage = showPageMessage && submit_page_message

  return (
    <div ref={pageMessageRef}>
      {showSubmitMessage ? (
        <PageMessage message={{ ...submit_page_message }} />
      ) : (
        <div className={'form'} data-t-id={trackingId}>
          <div className={'form__main-text'}>
            <h2 className={'form__main-text__headline'}>{headline}</h2>
            {description && (
              <Markdown className={'form__main-text__description'} markdown={description} />
            )}
          </div>
          <div className={'scriptForm'}>
            {mapFormScript !== null ? (
              <>
                <div data-form-block-id={mapFormScript.formBlockId} ref={formRef} />
                <div id={mapFormScript.id} />
                <div
                  className={'d365-mkt-config'}
                  data-hostname={mapFormScript.hostname}
                  data-website-id={mapFormScript.websiteId}
                  style={{ display: 'none' }}
                />
              </>
            ) : (
              <div className={'form__error'}>
                <SvgIcon color={'error'} name={'WARNING'} size={20} />
                Oops! Looks like this form is missing or not set up correctly.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
